/**
 * Recovery for a route chunk that fails to load.
 *
 * A deploy rotates ~200 content-hashed files and the old ones are pruned from
 * the server, so any page that has been open across a deploy is holding URLs
 * that no longer exist. The next lazy route it loads 404s. Verified in the prod
 * nginx access log: real devices (including iOS Safari) request pruned
 * /assets/*.js and get a 404.
 *
 * Reloading fixes that, because the HTML is served `no-cache`. It does NOT fix
 * it when a service worker is answering the navigation from a precache that has
 * lost entries — iOS evicts Cache Storage under pressure — because the reload
 * then returns the same stale document and the same dead URL. So recovery
 * purges the caches and unregisters the worker first, and only then reloads.
 *
 * The attempt is recorded with a timestamp and is deliberately never cleared on
 * a later success: the previous guard removed its flag on every successful
 * route load, so a page that failed every time simply reloaded on every click,
 * forever, and the error was never surfaced. A second failure inside the window
 * must reach the error boundary instead.
 */
export const CHUNK_RECOVERY_KEY = 'chunk-recovery-attempt';

/** How long one recovery attempt suppresses the next. */
const ATTEMPT_WINDOW_MS = 5 * 60 * 1000;

interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ChunkRecoveryDeps {
  storage: KeyValueStore;
  /** Drop the service worker and its caches so the reload can reach the network. */
  purge: () => Promise<void>;
  reload: () => void;
  now: () => number;
}

/**
 * @returns true if a reload was started (the caller must never settle),
 *          false if recovery was already tried and the error should surface.
 */
export async function recoverFromChunkFailure(deps: ChunkRecoveryDeps): Promise<boolean> {
  const { storage, purge, reload, now } = deps;

  const previous = Number(storage.getItem(CHUNK_RECOVERY_KEY));
  if (previous && now() - previous < ATTEMPT_WINDOW_MS) return false;

  storage.setItem(CHUNK_RECOVERY_KEY, String(now()));
  // Best effort — a browser without CacheStorage or a purge that throws must
  // still get the reload, which is what fixes the common (no-SW) case.
  try {
    await purge();
  } catch {
    /* fall through to the reload */
  }
  reload();
  return true;
}

/** Real purge: unregister every service worker and delete every cache it owns. */
export async function purgeAppCaches(): Promise<void> {
  if (typeof caches !== 'undefined') {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
  if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
}

/**
 * Does this error mean "this page is from a build that no longer exists on the
 * server"?
 *
 * Two shapes, both reproduced against production:
 *   - the lazy route chunk 404s ("Failed to fetch dynamically imported module")
 *   - vite-react-ssg's static loader manifest 404s. It fetches
 *     `static-loader-data-manifest-<build hash>.json` using the hash inlined in
 *     the document, with no error handling, so a pruned manifest returns the
 *     404 HTML page and `.json()` throws a SyntaxError about a leading "<".
 *     That one is a route LOADER failure, so it never passes through the lazy
 *     loader's own recovery — it lands straight on the error boundary.
 */
export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (!message) return false;
  return (
    /dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /is not valid JSON/i.test(message) ||
    /Unexpected token '<'/i.test(message) ||
    /^JSON\.parse/i.test(message)
  );
}
